import React, { useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Divider,
  CircularProgress,
  Switch,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";
import {
  Save,
  Cancel,
  Warning,
  Info,
  Help,
  CalendarToday,
  Euro,
  CheckCircle
} from "@material-ui/icons";
import styled from "styled-components";

// Importar hooks y servicios
import { useTourData } from "../../../hooks/useTourData";
import { useTourPricing } from "../../../hooks/useTourPricing";
import { useTourValidation } from "../../../hooks/useTourValidation";
import TourDataService from "../../../services/tourDataService";

// Importar componentes
import TourOptions from "./components/TourOptions";
import VehicleSelection from "./components/VehicleSelection";
import PricingCalculator from "./components/PricingCalculator";
import TourSummary from "../TourSummary";
import RentalCostSummary from "../RentalCostSummary";
import PricingValidationAlert from "../PricingValidationAlert";

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    max-width: 1400px;
    width: 95vw;
    max-height: 90vh;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 0;
`;

const MainContainer = styled.div`
  display: flex;
  height: 100%;
`;

const LeftSection = styled.div`
  flex: 1;
  padding: 24px;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
`;

const RightSection = styled.div`
  flex: 1;
  padding: 24px;
  background-color: #fafafa;
  overflow-y: auto;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  color: #1976d2;
  font-size: 1.25rem;
  line-height: 1.4;
  margin-bottom: 16px;
  border-bottom: 2px solid #1976d2;
  padding-bottom: 8px;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const FormSectionTitle = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 1rem;
  margin-bottom: 12px;
`;

const CustomAlert = styled.div`
  padding: 16px;
  border-radius: 4px;
  margin: 16px 0;
  
  ${props => props.severity === 'error' && `
    background-color: #ffebee;
    color: #c62828;
    border: 1px solid #f44336;
  `}
  
  ${props => props.severity === 'warning' && `
    background-color: #fff3e0;
    color: #ef6c00;
    border: 1px solid #ff9800;
  `}
  
  ${props => props.severity === 'info' && `
    background-color: #e3f2fd;
    color: #1565c0;
    border: 1px solid #2196f3;
  `}
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SummaryLabel = styled.div`
  font-weight: 500;
  color: #333;
`;

const SummaryValue = styled.div`
  font-weight: 600;
  color: #1976d2;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding: 16px;
  background-color: #f5f5f5;
  border-top: 1px solid #e0e0e0;
`;

/**
 * Componente principal del formulario de procesamiento de tours
 */
const TourProcessingForm = ({
  open,
  onClose,
  tourId,
  tourData = null,
  mode = "pending", // "pending" o "confirmed"
  onSuccess,
  onError
}) => {
  // Estados del formulario
  const [formData, setFormData] = useState({
    vehicle: {
      modelId: null,
      vehicleId: null,
      gpsDeviceId: null
    },
    options: {
      gps: false,
      insurance: false,
      cancelInsurance: false,
      passenger: false,
      singleRoom: false,
      lowSeat: false,
      single: false,
      double: true // Por defecto habitación doble
    },
    payment: {
      amount: 0,
      chargeType: "stripe",
      partial: 0
    },
    discounts: {
      discount: 0,
      rewards: 0
    },
    comments: {
      imtComment: "",
      clientComment: ""
    },
    settings: {
      noConfirmationEmail: false,
      confirmValue: false
    }
  });

  // Hooks personalizados
  const {
    models,
    vehicles,
    gpsDevices,
    loading: dataLoading,
    error: dataError,
    updateTourData,
    updateVehiclesAndGpsForModel
  } = useTourData(tourId, "create", tourData);

  const {
    pricing,
    pricingLoading,
    pricingError,
    updatePricing
  } = useTourPricing(tourData, formData.options, [], [], [], formData.discounts);

  const {
    errors,
    warnings,
    isValid,
    validateField,
    clearErrors
  } = useTourValidation(
    formData, 
    tourData, 
    pricing, 
    models || [], 
    vehicles || [], 
    gpsDevices || []
  );

  // Estados de UI
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Memoizar si el formulario está listo para enviar
  const isReadyToSubmit = useMemo(() => {
    return isValid && !dataLoading && !pricingLoading && !submitting && tourData;
  }, [isValid, dataLoading, pricingLoading, submitting, tourData]);

  // Callbacks optimizados
  const handleVehicleChange = useCallback((newVehicle) => {
    setFormData(prev => ({
      ...prev,
      vehicle: newVehicle
    }));

    // Si cambió el modelo, actualizar vehículos y GPS (como en el componente original)
    if (newVehicle.modelId && newVehicle.modelId !== formData.vehicle.modelId) {
      console.log('Model changed, updating vehicles and GPS for model:', newVehicle.modelId);
      updateVehiclesAndGpsForModel(newVehicle.modelId);
    }
  }, [formData.vehicle.modelId, updateVehiclesAndGpsForModel]);

  const handleOptionsChange = useCallback((newOptions) => {
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  }, []);

  const handlePaymentChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      payment: {
        ...prev.payment,
        [field]: value
      }
    }));
  }, []);

  const handleDiscountChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      discounts: {
        ...prev.discounts,
        [field]: value
      }
    }));
  }, []);

  const handleCommentChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      comments: {
        ...prev.comments,
        [field]: value
      }
    }));
  }, []);

  const handleSettingChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value
      }
    }));
  }, []);

  // Función de envío del formulario
  const handleSubmit = useCallback(async () => {
    if (!isReadyToSubmit) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const submitData = {
        tourId,
        vehicle: formData.vehicle,
        options: formData.options,
        payment: formData.payment,
        discounts: formData.discounts,
        comments: formData.comments,
        settings: formData.settings,
        pricing: pricing
      };

      let result;
      if (mode === "pending") {
        result = await TourDataService.updatePendingTour(submitData);
      } else {
        result = await TourDataService.updateConfirmedTour(submitData);
      }

      if (result.success) {
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
      } else {
        setSubmitError(result.error || "Error al procesar el tour");
      }
    } catch (error) {
      setSubmitError(error.message || "Error inesperado");
      if (onError) {
        onError(error);
      }
    } finally {
      setSubmitting(false);
    }
  }, [isReadyToSubmit, tourId, formData, pricing, mode, onSuccess, onClose, onError]);

  // Renderizar errores
  if (dataError) {
    return (
      <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Error al Cargar Datos</DialogTitle>
        <StyledDialogContent>
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Warning color="error" style={{ fontSize: 48, marginBottom: 16 }} />
            <Typography variant="h6" color="error" gutterBottom>
              Error al cargar la información del tour
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {dataError}
            </Typography>
          </div>
        </StyledDialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </StyledDialog>
    );
  }

  // Renderizar loading
  if (dataLoading || !tourData || dataError) {
    return (
      <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Procesando Tour</DialogTitle>
        <StyledDialogContent>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 40 }}>
            <CircularProgress size={48} />
            <Typography variant="body1" style={{ marginLeft: 16 }}>
              Cargando información del tour...
            </Typography>
          </div>
        </StyledDialogContent>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h5">
            {mode === "pending" ? "PROCESAR TOUR PENDIENTE" : "PROCESAR TOUR CONFIRMADO"}
            {tourData && tourData.driver && ` PARA: ${tourData.driver.first_name} ${tourData.driver.last_name}`}
          </Typography>
          <Tooltip title="Ayuda">
            <IconButton size="small">
              <Help fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </DialogTitle>

      <StyledDialogContent>
        <MainContainer>
          {/* Sección Izquierda - Formulario de Edición */}
          <LeftSection>
            <SectionTitle>Configuración del Tour</SectionTitle>

            {/* Validación de precios */}
            <PricingValidationAlert 
              tourData={tourData}
              pricing={pricing}
              onApplyCorrections={(correctedPricing) => {
                // Actualizar el valor a cargar con los precios corregidos
                if (correctedPricing && correctedPricing.total) {
                  handlePaymentChange('amount', correctedPricing.total);
                }
              }}
            />

            {/* Errores y advertencias */}
            {Object.keys(errors).length > 0 && (
              <CustomAlert severity="error">
                <Typography variant="body2">
                  <strong>Errores encontrados:</strong>
                </Typography>
                {Object.entries(errors).map(([field, error]) => (
                  <Typography key={field} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </CustomAlert>
            )}

            {warnings.length > 0 && (
              <CustomAlert severity="warning">
                <Typography variant="body2">
                  <strong>Advertencias:</strong>
                </Typography>
                {warnings.map((warning, index) => (
                  <Typography key={index} variant="body2">
                    • {warning}
                  </Typography>
                ))}
              </CustomAlert>
            )}

            {submitError && (
              <CustomAlert severity="error">
                <Typography variant="body2">
                  <strong>Error al procesar:</strong> {submitError}
                </Typography>
              </CustomAlert>
            )}

            {/* Selección de Vehículo */}
            <FormSection>
              <FormSectionTitle>Selección de Vehículo</FormSectionTitle>
              <VehicleSelection
                vehicle={formData.vehicle}
                models={models}
                vehicles={vehicles}
                gpsDevices={gpsDevices}
                onVehicleChange={handleVehicleChange}
                disabled={dataLoading}
                warnings={warnings.filter(w => w.toLowerCase().includes('vehículo') || w.toLowerCase().includes('gps'))}
                info={[]}
              />
            </FormSection>

            {/* Opciones del Tour */}
            <FormSection>
              <FormSectionTitle>Opciones del Tour</FormSectionTitle>
              <TourOptions
                options={formData.options}
                onChange={handleOptionsChange}
                disabled={dataLoading}
                modelName={models && Array.isArray(models) && models.find(m => m.id === formData.vehicle.modelId) ? 
                         models.find(m => m.id === formData.vehicle.modelId).model_name : null}
                hasPassenger={tourData && tourData.booking && tourData.booking.has_passenger}
                warnings={warnings.filter(w => w.toLowerCase().includes('opción') || w.toLowerCase().includes('seguro'))}
                info={[]}
                tourData={tourData}
              />
            </FormSection>

            {/* Información de Pago */}
            <FormSection>
              <FormSectionTitle>Información de Pago</FormSectionTitle>
              
              {/* Alerta informativa sobre pago parcial */}
              {/* <CustomAlert severity="info" style={{ marginBottom: 16 }}>
                <Typography variant="body2">
                  <strong>Información sobre Pago:</strong>
                </Typography>
                <Typography variant="body2">
                  • El "Valor Total del Tour" muestra el costo total calculado (no editable)
                </Typography>
                <Typography variant="body2">
                  • El "Pago Parcial" determina el monto que se va a cobrar realmente
                </Typography>
                <Typography variant="body2">
                  • Si "Pago Parcial" es 0, se cobrará el valor total del tour
                </Typography>
                <Typography variant="body2">
                  • Si "Pago Parcial" es mayor a 0, se cobrará ese monto específico
                </Typography>
                <Typography variant="body2">
                  • La factura se generará basada en el monto que se vaya a cobrar
                </Typography>
              </CustomAlert> */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Valor Total del Tour (€)"
                    type="number"
                    value={pricing && pricing.total ? pricing.total : 0}
                    disabled={true}
                    InputProps={{
                      startAdornment: <Euro fontSize="small" style={{ marginRight: 8 }} />
                    }}
                    helperText="Valor total calculado del tour (no editable)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Cargo</InputLabel>
                    <Select
                      value={formData.payment.chargeType}
                      onChange={(e) => handlePaymentChange('chargeType', e.target.value)}
                      disabled={pricingLoading}
                    >
                      <MenuItem value="stripe">Stripe</MenuItem>
                      <MenuItem value="p4m">P4M</MenuItem>
                      <MenuItem value="other">Otro (Dinero, transferencia bancaria)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Pago Parcial (€)"
                    type="number"
                    value={formData.payment.partial}
                    onChange={(e) => handlePaymentChange('partial', parseFloat(e.target.value) || 0)}
                    disabled={pricingLoading}
                    helperText="Si es diferente de 0, se cobrará este monto en lugar del valor total"
                    InputProps={{
                      startAdornment: <Euro fontSize="small" style={{ marginRight: 8 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Descuento (€)"
                    type="number"
                    value={formData.discounts.discount}
                    onChange={(e) => handleDiscountChange('discount', parseFloat(e.target.value) || 0)}
                    disabled={pricingLoading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Recompensas (€)"
                    type="number"
                    value={formData.discounts.rewards}
                    onChange={(e) => handleDiscountChange('rewards', parseFloat(e.target.value) || 0)}
                    disabled={pricingLoading}
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* Comentarios */}
            <FormSection>
              <FormSectionTitle>Comentarios</FormSectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Comentarios IMT (si el tipo de cargo no es Stripe)"
                    multiline
                    rows={3}
                    value={formData.comments.imtComment}
                    onChange={(e) => handleCommentChange('imtComment', e.target.value)}
                    disabled={submitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Comentarios del Cliente"
                    multiline
                    rows={3}
                    value={formData.comments.clientComment}
                    onChange={(e) => handleCommentChange('clientComment', e.target.value)}
                    disabled={submitting}
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* Configuraciones Adicionales */}
            <FormSection>
              <FormSectionTitle>Configuraciones Adicionales</FormSectionTitle>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.settings.confirmValue}
                      onChange={(e) => handleSettingChange('confirmValue', e.target.checked)}
                      disabled={submitting}
                    />
                  }
                  label="Confirmar valor a cargar"
                />
              </div>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.settings.noConfirmationEmail}
                      onChange={(e) => handleSettingChange('noConfirmationEmail', e.target.checked)}
                      disabled={submitting}
                    />
                  }
                  label="No enviar correo de confirmación"
                />
              </div>
            </FormSection>
          </LeftSection>

          {/* Sección Derecha - Resumen e Historial */}
          <RightSection>
            <SectionTitle>Resumen e Historial</SectionTitle>

            {/* Tour Summary con cálculos corregidos */}
            {tourData && (
              <FormSection>
                <TourSummary 
                  tourData={tourData}
                  pricing={pricing}
                  showCorrections={true}
                />
              </FormSection>
            )}

            {/* Rental Cost Summary con cálculos corregidos */}
            {tourData && (
              <FormSection>
                <RentalCostSummary 
                  tourData={tourData}
                  pricing={pricing}
                  showCorrections={true}
                />
              </FormSection>
            )}

            {/* Cálculo de Precios */}
            <FormSection>
              <FormSectionTitle>Cálculo de Precios</FormSectionTitle>
              <PricingCalculator
                pricing={pricing}
                onAmountChange={(amount) => handlePaymentChange('amount', amount)}
                disabled={pricingLoading}
                warnings={warnings.filter(w => w.toLowerCase().includes('precio') || w.toLowerCase().includes('pago'))}
                info={[]}
                tourData={tourData}
              />
            </FormSection>

            {/* Información de Pago del Cliente */}
            {tourData && tourData.driver && (
              <FormSection>
                <FormSectionTitle>Información de Pago del Cliente</FormSectionTitle>
                <div>
                  <SummaryItem>
                    <SummaryLabel>Pasaporte:</SummaryLabel>
                    <SummaryValue>N/A</SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Licencia de Conducir:</SummaryLabel>
                    <SummaryValue>N/A</SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Datos de Tarjeta:</SummaryLabel>
                    <SummaryValue>visa - 0000000000000000 - 10/2025 - 471</SummaryValue>
                  </SummaryItem>
                </div>
              </FormSection>
            )}
          </RightSection>
        </MainContainer>

        {/* Botones de Acción */}
        <ActionButtons>
          <div>
            <Button onClick={onClose} startIcon={<Cancel />}>
              Cancelar
            </Button>
          </div>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!isReadyToSubmit || submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
              endIcon={!submitting && <CheckCircle />}
            >
              {submitting ? "Procesando..." : "GUARDAR"}
            </Button>
          </div>
        </ActionButtons>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default TourProcessingForm; 


// WEBPACK FOOTER //
// ./src/components/tour/TourProcessing/TourProcessingForm.js